import {Injectable} from "@angular/core";
import {CoreHttpClientApiBaseService} from "@core/services/http-client-api-base/core-http-client-api-base.service";
import {Observable} from "rxjs";
import {
  Clients,
  CombineOperator,
  FilterDto,
  FilterOperators,
  FilterTypes,
  GroupedFilterDto,
  InitIssueChatDto, IssueContentType,
  IssueListDto,
  IssueTemplateListDto,
  IssueType,
  Orders
} from "@server-models";
import {IPaginationParams} from "@shared/interfaces/pagination-params.interface";
import {ILocalPageOutputDto} from "@shared/interfaces/orga-response.interface";
import {IRequestOptions} from "@shared/interfaces/request-options.interface";
import {CoreEnvironmentService} from "@core/services/environment/core-environment.service";

@Injectable({
  providedIn: 'root',
})
export class SharedIssueApiService extends CoreHttpClientApiBaseService {

  private defaultPageSize = CoreEnvironmentService.getEnvironment()?.apiUrl?.pageDefaultSize ?? 25;

  requestInitChat(issueId: any): Observable<InitIssueChatDto> {
    return super.post<InitIssueChatDto>(`/tech/issue/${ issueId }/chat/start`, undefined);
  }

  markIssueAsRead(issueId: any): Observable<void> {
    return super.post<void>(`/tech/issue/${ issueId }/notification/read`, undefined);
  }

  getAllIssueList(filters: FilterDto[], options: IRequestOptions): Observable<ILocalPageOutputDto<IssueListDto[]>> {

    // Exclude issue types not supported
    filters = filters ?? [];
    filters.push(this._getExcludedIssueFilter());

    return super.post<ILocalPageOutputDto<IssueListDto[]>>(`/tech/issue/all/filter`, undefined, filters, options)
  }

  getIssueRecentList(params: IPaginationParams,
                     filters?: FilterDto[]): Observable<ILocalPageOutputDto<IssueListDto[]>> {

    // recent is for link app
    const options = {
      params: {
        PageSize: this.defaultPageSize.toString(),
        PageNumber: params.pageNumber.toString(),
        Cols: params.cols,
        SortField: "lastModified",
        Sort: Orders.Descending.toString()
      }
    }

    // Exclude issue types not supported
    filters = filters ?? [];
    filters.push(this._getExcludedIssueFilter());

    return super.post<ILocalPageOutputDto<IssueListDto[]>>(`/tech/issue/recent/filter`, undefined, filters, options);
  }

  getIssueTemplateList(filters?: FilterDto[],
                       pageNumber: number = 1): Observable<ILocalPageOutputDto<IssueTemplateListDto[]>> {
    const options = {
      params: {
        PageSize: this.defaultPageSize.toString(),
        PageNumber: pageNumber.toString(),
      }
    }

    // Exclude issue types not supported
    filters = filters ?? [];
    filters.push(this._getExcludedIssueTemplateFilter());

    return super.post<ILocalPageOutputDto<IssueTemplateListDto[]>>(`/tech/issue/template/filter`, undefined, filters, options);
  }

  getIssueTemplateListByResource(resourceId: number, filters: FilterDto[],
                                 pageNumber: number = 1): Observable<ILocalPageOutputDto<IssueTemplateListDto[]>> {
    const options = {
      params: {
        PageSize: this.defaultPageSize.toString(),
        PageNumber: pageNumber.toString(),
      }
    }
    return super.post<ILocalPageOutputDto<IssueTemplateListDto[]>>(`/tech/resource/${ resourceId }/issuetemplate/filter`, undefined, filters, options);
  }

  private _getExcludedIssueFilter(): GroupedFilterDto {
    return {
      type: FilterTypes.Grouped,
      combinedAs: CombineOperator.Or,
      children: [
        {
          property: 'issueType',
          operator: FilterOperators.NotEqual,
          value: IssueType.Order.toString()
        },
        ...(this.clientType !== Clients.Team ? [] : [{
          property: 'issueType',
          operator: FilterOperators.NotEqual,
          value: IssueType.Connected.toString(),
        }])
      ]
    }
  }

  private _getExcludedIssueTemplateFilter(): GroupedFilterDto {
    return {
      type: FilterTypes.Grouped,
      combinedAs: CombineOperator.Or,
      children: [
        {
          property: "contentType",
          value: IssueContentType.WinterhalterServiceCall.toString(),
          operator: FilterOperators.NotEqual
        },
        {
          property: "contentType",
          value: IssueContentType.WinterhalterProductOrder.toString(),
          operator: FilterOperators.NotEqual
        },
        ...(this.clientType !== Clients.Team ? [] : [{
          property: 'contentType',
          value: IssueContentType.Connected.toString(),
          operator: FilterOperators.NotEqual,
        }])
      ]
    }
  }
}
